import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { useAppDispatch } from '@/Application/Hooks/Redux';
import type { GiftCardFormFields } from '@/modules/cart/domain/Cart';
import { addGiftCard } from '@/modules/cart/infrastructure/CartReduxRepository';
import { CardInput } from '@/modules/perseo-billing/components';
import { yupResolver } from '@/modules/perseo-billing/containers/payment-methods/components/cards/cards.schema';
import { formatCardNumber } from '@/modules/perseo-billing/containers/payment-methods/components/cards/utils';
import { AddGiftCardFormFooter } from '@/modules/perseo-billing/containers/payment-methods/components/gift-card/add-gift-card/add-gift-card-form-footer.component';
import { schema } from '@/modules/perseo-billing/containers/payment-methods/components/gift-card/giftcard.schema';
import { WhereToFindLink } from '@/modules/perseo-billing/containers/payment-methods/components/gift-card/link-where-can-i-find-it.component';
import { NotSubmittedGiftCardPreview } from '@/modules/perseo-billing/containers/payment-methods/components/gift-card/not-submitted-gift-card-preview.component';
import { useGtmBillingProduct } from '@/modules/perseo-billing/hooks/use-gtm-billing';
import { setIsKeyboardOpen } from '@/modules/perseo-billing/store/slices/billing';
import type { IconTypes } from '@/modules/perseo-billing/ui/react/src/components/beta';
import { Icon } from '@/modules/perseo-billing/ui/react/src/components/beta';
import type { TIconName } from '@/modules/perseo-billing/ui/react/src/components/gamma';
import { PdsIcon } from '@/modules/perseo-billing/ui/react/src/components/gamma';
import { LocalHookFormDevtool } from '@/sections/shared/hooks/local-hookform-devtool';

const DEFAULT_FORM_VALUES: GiftCardFormFields = {
  cardNumber: '',
  cardCvv: ''
};

export const GiftCardForm = () => {
  const dispatch = useAppDispatch();

  const { sendPaymentErrorGtm: sendPaymentGiftCardErrorGtm, addGiftCardGtm } =
    useGtmBillingProduct();

  const giftCardForm = useForm<GiftCardFormFields>({
    resolver: yupResolver(schema),
    defaultValues: DEFAULT_FORM_VALUES,
    mode: 'onChange'
  });

  const handleSubmitForm = (data: GiftCardFormFields) => {
    const giftcardNumber = String(data?.cardNumber).replace(/\s/g, '');

    const giftCardData = {
      cardNumber: giftcardNumber,
      cardCvv: data.cardCvv
    };

    dispatch(
      addGiftCard({
        ...giftCardData,
        sendPaymentGiftCardErrorGtm,
        addGiftCardGtm
      })
    );
  };

  const { handleSubmit } = giftCardForm;

  const [isPasswordHidden, setIsPasswordHidden] = useState(true);

  const { formState, setValue, setFocus, register } = giftCardForm;
  const { errors, touchedFields, dirtyFields } = formState;

  useEffect(() => {
    setFocus('cardNumber');
  }, [setFocus]);

  const iconColor = errors?.cardNumber
    ? 'co-fill-pds-error-500'
    : 'co-fill-pds-neutral-900';

  const cardNumberInputIcon: TIconName = errors?.cardNumber
    ? 'PdsInfoCircle'
    : 'PdsOutlineGiftCard';

  const handleCardNumber = (cardNumber: string) => {
    const value = formatCardNumber(cardNumber);

    if (value.length <= 19) {
      setValue('cardNumber', value);
    } else {
      setFocus('cardCvv');
    }
  };

  const handleCardCvv = (cardCvv: string) => {
    if (cardCvv.length <= 4) {
      setValue('cardCvv', cardCvv);
    }
  };

  const isFieldValid = (name: keyof GiftCardFormFields) =>
    Boolean(touchedFields[name]) && Boolean(dirtyFields[name]) && !errors[name];

  const getIconForPasswordInput = (): IconTypes => {
    if (errors?.cardCvv) {
      return 'InputWarningIcon';
    }
    return isPasswordHidden ? 'EyeBlockIcon' : 'EyeIcon';
  };

  const cardNumberError = errors?.cardNumber?.message?.toString();
  const passwordError = errors?.cardCvv?.message?.toString();

  const cardNumberEndIcon = (
    <div className="co-flex co-justify-center co-items-center co-flex-shrink-0 co-p-2">
      <PdsIcon icon={cardNumberInputIcon} className={iconColor} />
    </div>
  );

  const passwordEndIcon = <Icon icon={getIconForPasswordInput()} />;

  return (
    <FormProvider {...giftCardForm}>
      <form
        onSubmit={handleSubmit(handleSubmitForm)}
        className="co-flex co-flex-col co-gap-4"
      >
        <div className="co-flex co-gap-24px">
          <div className="co-flex co-flex-col co-gap-y-16px co-w-full lg:co-w-1/2">
            <WhereToFindLink />
            <div className="co-flex co-flex-col co-gap-16px co-w-full">
              <CardInput
                type="tel"
                inputMode="numeric"
                example="Ejemplo: 1234 3456 6789 3456"
                error={cardNumberError}
                endIcon={cardNumberEndIcon}
                label={'Número de tarjeta'}
                maxLength={19}
                isValid={isFieldValid('cardNumber')}
                onFocus={() => {
                  dispatch(setIsKeyboardOpen(true));
                }}
                {...register('cardNumber', {
                  onChange: (e) => handleCardNumber(e.target.value)
                })}
              />

              <CardInput
                type={isPasswordHidden ? 'password' : 'text'}
                example="Escríbelo como aparece en la tarjeta"
                error={passwordError}
                label={'Clave'}
                endIcon={passwordEndIcon}
                onEndIconClick={() => setIsPasswordHidden(!isPasswordHidden)}
                maxLength={4}
                isValid={isFieldValid('cardCvv')}
                onFocus={() => {
                  dispatch(setIsKeyboardOpen(true));
                }}
                {...register('cardCvv', {
                  onChange(event) {
                    handleCardCvv(event.target.value);
                  }
                })}
              />
            </div>
          </div>
          <NotSubmittedGiftCardPreview />
        </div>
        <AddGiftCardFormFooter handleSubmit={handleSubmit(handleSubmitForm)} />
      </form>
      <LocalHookFormDevtool control={giftCardForm?.control} />
    </FormProvider>
  );
};
